/* Copyright (c) 2020-present CloudIO Inc */

import { metricSamplePage } from 'componentregistry/components/MetricBox/MetricBoxManager';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn, Item } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './MetricColorAndIcon';
import { ItemType } from 'componentregistry/registry/Base.type';

const page: typeof metricSamplePage & {
  metricBox: Item<'MetricBox'>;
} = {
  ...metricSamplePage,
  columnPanel1: {
    ...metricSamplePage.columnPanel1,
    children: ['metricBox'],
  },
  metricBox: {
    children: ['child'],
    itemId: 'metricBox',
    parent: 'columnPanel1',
    props: {
      title: { type: 'text', value: 'Processed' },
      value: { type: 'text', value: '50,000' },
      trend: { type: 'text', value: '1' },
      trendDescription: {
        type: 'text',
        value: '10% compared to last month',
      },
      colorAndIconSwitchCondition: { type: 'text', value: 'X' },
      metricStyle: 'Alpha',
      minWidth: 300,
    },
    type: 'MetricBox',
  },
};

const MetricColorAndIconManager: ComponentManagerFn<'MetricColorAndIcon'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'MetricColorAndIcon'>(
      platform,
      'MetricColorAndIcon',
    ),
    type: 'MetricColorAndIcon',
    name: 'Color And Icon',
    isChildAllowed: (_page, item, childType) =>
      ['TriggerOnClick'].includes(childType as ItemType),
    defaultValue,
    props: {
      caseValue: {
        label: 'Case Value',
        type: 'string',
        helpText:
          'Provide a constant string value (without quotes) for this case to match the switch expression of the parent Metric Box component. An empty case will be matched with any switch expression.',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
      },
    },
    category: 'Dashboard',
    icon: (item) => {
      if (item.props.icon?.iconName) {
        return item.props.icon;
      }
      return { iconName: 'icons' };
    },
    getDisplayText: (item) => item.props.caseValue,
    description: `**Overview**<br/>
The Metric Color And Icon component is a helper configuration node used exclusively inside a Metric Box. It allows you to dynamically change the icon and color of the metric based on data conditions (e.g., turning a KPI "Red" if values drop below a threshold, or "Green" if targets are met).
It functions like a case statement in programming: if the parent Metric Box's "Switch Expression" matches this component's "Case Value," then these styles are applied.

**1. Logic & Matching (The "Case")**<br/>
This component does not evaluate logic itself; it waits for a value from its parent.
* **Case Value:** A static string (without quotes) that represents a specific state. (Example: If your Metric Box expression returns "Critical", set this Case Value to Critical).
* **Default Case:** If you leave this field Empty, this component acts as the "Default" or "Fallback" style, applied when no other specific cases match.

**2. Visual Configuration**<br/>
When the case matches, these settings override the default appearance of the Metric Box.
* **Icon:** Selects the specific icon to display (e.g., Check Circle for success, Warning Triangle for alerts).
* **Color:** Sets the theme color for the icon and metric value (e.g., Primary, Secondary, Success, Error).

**3. Implementation Guide**<br/>
To set up dynamic KPI styling:
1. **Parent (Metric Box):** Define a Switch Expression that returns a status string (e.g., \`value < 0 ? "Negative" : "Positive"\`).
2. **Child 1 (Metric Color & Icon):** Set Case Value to **Negative**, Color to **Error**, and Icon to **Trending Down**.
3. **Child 2 (Metric Color & Icon):** Set Case Value to **Positive**, Color to **Success**, and Icon to **Trending Up**.

**4. Triggers**<br/>
* **TriggerOnClick:** While the parent Metric Box handles the main layout, you can add a TriggerOnClick to this specific case. This allows you to run different logic depending on the state (e.g., clicking a "Critical" metric opens an urgent report, while clicking a "Normal" metric does nothing).`,
    helpRenders: [
      {
        title: 'Color And Icon',
        description:
          'You can specify different icon & color for each matching case values.',
        page,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['icon', 'color'],
        item: {
          children: [],
          itemId: 'child',
          parent: 'metricBox',
          props: {
            caseValue: 'X',
            icon: { iconName: 'analytics', iconType: 'light' },
            color: 'secondary',
          },
          type: 'MetricColorAndIcon',
        },
      },
    ],
  };
};

export default MetricColorAndIconManager;
